import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { GuidService } from 'ui-components/services/guid-service';
import { Button } from 'ui-components/components/button';
import classnames from 'classnames';
import { ServiceRow } from './service-row';
import messages from './messages';
import style from './style.module.less';

export const ServicePackageAndConfirmation = ({ isFormValid, onServicesConfirmed, unsavedServices = [] }) => {
  const mappedServices = useMemo(
    () =>
      unsavedServices.map((service) => ({
        ...service,
        temporaryUseId: GuidService.newGuid(),
      })),
    [unsavedServices],
  );
  const hasAnyUnassignedServices = useMemo(
    () => unsavedServices.some((service) => !service.carrierId || !service.sellerLabelProviderId || !service.serviceId),
    [unsavedServices],
  );
  const onServiceChange = useCallback(
    (service) => {
      const updatedServices = mappedServices.map((unsavedService) => {
        return unsavedService.temporaryUseId === service.temporaryUseId ? service : unsavedService;
      });
      onServicesConfirmed(updatedServices);
    },
    [mappedServices, onServicesConfirmed],
  );
  const _onServiceRemoved = useCallback(
    (temporaryUseId) => {
      const updatedServices = mappedServices.filter((unsavedService) => unsavedService.temporaryUseId !== temporaryUseId);
      onServicesConfirmed(updatedServices);
    },
    [mappedServices, onServicesConfirmed],
  );

  const onAddNewService = useCallback(() => {
    const newService = {
      carrierId: null,
      confirmationId: null,
      sellerLabelProviderId: null,
      serviceId: null,
      packageTypeId: null,
      temporaryUseId: GuidService.newGuid(),
    };
    onServicesConfirmed([...mappedServices, newService]);
  }, [mappedServices, onServicesConfirmed]);

  const onPackageChange = useCallback(
    (temporaryUseId, packageTypeId, packageName) => {
      const updatedServices = mappedServices.map((unsavedService) => {
        return unsavedService.temporaryUseId === temporaryUseId ? { ...unsavedService, packageTypeId, packageName } : unsavedService;
      });
      onServicesConfirmed(updatedServices);
    },
    [mappedServices, onServicesConfirmed],
  );

  const onConfirmationChange = useCallback(
    (temporaryUseId, confirmationId, confirmationName) => {
      const updatedServices = mappedServices.map((unsavedService) => {
        return unsavedService.temporaryUseId === temporaryUseId ? { ...unsavedService, confirmationId, confirmationName } : unsavedService;
      });
      onServicesConfirmed(updatedServices);
    },
    [mappedServices, onServicesConfirmed],
  );

  return (
    <div className={style['service-package-and-confirmation']}>
      <div className={style['advanced-instructional-text']}>
        <FormattedMessage {...messages.serviceSelectorInstructionalText} />
      </div>
      <div className={style['service-package-header']}>
        <div>
          <FormattedMessage {...messages.serviceHeaderText} />
        </div>
        <div>
          <FormattedMessage {...messages.packageTypeHeaderText} />
        </div>
        <div>
          <FormattedMessage {...messages.confirmationTypeHeaderText} />
        </div>
      </div>
      {mappedServices.map(({ carrierId, confirmationId, sellerLabelProviderId, serviceId, packageTypeId, temporaryUseId }) => (
        <ServiceRow
          key={temporaryUseId}
          carrierId={carrierId}
          confirmationId={confirmationId}
          isFormValid={isFormValid}
          onConfirmationChange={onConfirmationChange}
          onPackageChange={onPackageChange}
          onServiceChange={onServiceChange}
          onServiceRemoved={_onServiceRemoved}
          packageTypeId={packageTypeId}
          sellerLabelProviderId={sellerLabelProviderId}
          serviceId={serviceId}
          temporaryUseId={temporaryUseId}
        />
      ))}
      <div className={style['service-package-footer']}>
        <FontAwesomeIcon
          className={classnames(style['add-service-icon'], { [style['icon-disabled']]: hasAnyUnassignedServices })}
          icon={faPlus}
        />
        <Button disabled={hasAnyUnassignedServices} onClick={onAddNewService} theme='link'>
          <FormattedMessage {...messages.addServiceButtonText} />
        </Button>
      </div>
    </div>
  );
};
